import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import request from "../../utils/request";

const SAMPLE_CSV = `email,firstName,lastName,companyName
host@example.com,Sam,Host,Bellhopt Villas`;

function parseCsv(text) {
  const rows = [];
  let cell = "";
  let row = [];
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"' && quoted && next === '"') {
      cell += '"';
      i += 1;
    } else if (ch === '"') {
      quoted = !quoted;
    } else if (ch === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((ch === "\n" || ch === "\r") && !quoted) {
      if (ch === "\r" && next === "\n") i += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);

  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const emailIdx = headers.findIndex((h) => ["email", "email address", "address"].includes(h));
  const firstIdx = headers.findIndex((h) => ["firstname", "first_name", "first name"].includes(h));
  const lastIdx = headers.findIndex((h) => ["lastname", "last_name", "last name"].includes(h));
  const companyIdx = headers.findIndex((h) =>
    ["company", "companyname", "company_name", "company name"].includes(h)
  );

  const dataRows = emailIdx >= 0 ? rows.slice(1) : rows;
  return dataRows
    .map((values) => ({
      email: (values[emailIdx >= 0 ? emailIdx : 0] || "").trim(),
      firstName: firstIdx >= 0 ? (values[firstIdx] || "").trim() : "",
      lastName: lastIdx >= 0 ? (values[lastIdx] || "").trim() : "",
      companyName: companyIdx >= 0 ? (values[companyIdx] || "").trim() : "",
    }))
    .filter((row) => row.email && row.email.includes("@"));
}

function recipientLabel(r) {
  const name = [r.firstName, r.lastName].filter(Boolean).join(" ").trim();
  return name ? `${name} <${r.email}>` : r.email;
}

export default function HostMarketingBlastModal({ open, onClose, adminInfo }) {
  const [audience, setAudience] = useState("all_hosts");
  const [from, setFrom] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const aliases = useMemo(
    () =>
      Array.from(
        new Set(
          [
            adminInfo?.gmailPrimaryAddress,
            adminInfo?.gmail,
            adminInfo?.email,
            ...(adminInfo?.gmailAliases || []),
          ].filter(Boolean)
        )
      ),
    [adminInfo]
  );

  useEffect(() => {
    if (open && !from && aliases[0]) setFrom(aliases[0]);
  }, [aliases, from, open]);

  const recipients = useMemo(() => parseCsv(csvText), [csvText]);
  const canPreview = audience === "all_hosts" || recipients.length > 0;
  const canSend = canPreview && subject.trim() && body.trim() && !sending;

  const payload = () => ({
    audience,
    from,
    subject,
    body,
    recipients: audience === "csv" ? recipients : [],
  });

  const loadPreview = async () => {
    setError("");
    setResult(null);
    setLoadingPreview(true);
    try {
      const { data } = await request.post("/leads/host-blast/preview", payload(), {
        withCredentials: true,
      });
      setPreview(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendBlast = async () => {
    if (!canSend) return;
    const count = preview?.count ?? (audience === "csv" ? recipients.length : 0);
    const confirmed = window.confirm(
      `Send this email blast to ${count || "the selected"} host recipient${count === 1 ? "" : "s"}?`
    );
    if (!confirmed) return;

    setError("");
    setResult(null);
    setSending(true);
    try {
      const { data } = await request.post("/leads/host-blast/send", payload(), {
        withCredentials: true,
      });
      setResult(data);
      setPreview(data ? { count: data.total, sample: preview?.sample || [] } : preview);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    const text = await file.text();
    setCsvText(text);
    setAudience("csv");
    setPreview(null);
    setResult(null);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-200">
                <div className="border-b border-zinc-200 px-5 py-4">
                  <Dialog.Title className="text-base font-semibold text-zinc-900">
                    Host marketing email blast
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-zinc-500">
                    Send a one-off marketing email to all hosts or to a CSV list. Supports merge
                    fields like <code>{"{{first_name|there}}"}</code>, <code>{"{{company_name}}"}</code>, and <code>{"{{email}}"}</code>.
                  </p>
                </div>

                <div className="grid gap-4 p-5 md:grid-cols-[220px_1fr]">
                  <aside className="space-y-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAudience("all_hosts");
                        setPreview(null);
                        setResult(null);
                      }}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-sm ${
                        audience === "all_hosts"
                          ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                          : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                      }`}
                    >
                      <span className="block font-semibold">All hosts</span>
                      <span className="text-xs text-zinc-500">Pull emails from host collection</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAudience("csv");
                        setPreview(null);
                        setResult(null);
                      }}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-sm ${
                        audience === "csv"
                          ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                          : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                      }`}
                    >
                      <span className="block font-semibold">Import CSV</span>
                      <span className="text-xs text-zinc-500">Use your own recipient list</span>
                    </button>

                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
                      <p className="font-semibold text-zinc-800">CSV columns</p>
                      <p className="mt-1">Required: <code>email</code></p>
                      <p>Optional: <code>firstName</code>, <code>lastName</code>, <code>companyName</code></p>
                    </div>
                  </aside>

                  <section className="space-y-3">
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        From
                      </label>
                      {aliases.length ? (
                        <select
                          value={from}
                          onChange={(e) => setFrom(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                        >
                          {aliases.map((alias) => (
                            <option key={alias} value={alias}>
                              {alias}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={from}
                          onChange={(e) => setFrom(e.target.value)}
                          placeholder="Connected Gmail sender"
                          className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        Subject
                      </label>
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="A quick update from Bellhopt"
                        className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        Email body
                      </label>
                      <textarea
                        rows={8}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={"Hi {{first_name|there}},\n\nWe wanted to share..."}
                        className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                      />
                      <p className="mt-1 text-xs text-zinc-500">
                        Plain text is fine. HTML is also supported if pasted here.
                      </p>
                    </div>

                    {audience === "csv" ? (
                      <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            CSV recipients
                          </label>
                          <input
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            className="text-xs"
                          />
                        </div>
                        <textarea
                          rows={5}
                          value={csvText}
                          onChange={(e) => {
                            setCsvText(e.target.value);
                            setPreview(null);
                            setResult(null);
                          }}
                          placeholder={SAMPLE_CSV}
                          className="mt-2 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 font-mono text-xs focus:border-indigo-500 focus:outline-none"
                        />
                        <p className="mt-1 text-xs text-zinc-500">
                          Parsed recipients: <span className="font-semibold text-zinc-800">{recipients.length}</span>
                        </p>
                      </div>
                    ) : null}

                    {error ? (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                      </div>
                    ) : null}

                    {preview ? (
                      <div className="rounded-xl border border-zinc-200 bg-white p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-zinc-900">
                            {preview.count ?? preview.total} recipients
                          </p>
                          <button
                            type="button"
                            onClick={loadPreview}
                            className="text-xs font-medium text-indigo-600 hover:underline"
                          >
                            Refresh preview
                          </button>
                        </div>
                        {(preview.sample || []).length ? (
                          <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto text-xs text-zinc-600">
                            {preview.sample.map((r) => (
                              <li key={r.email} className="truncate">
                                {recipientLabel(r)}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}

                    {result ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                        Sent {result.sent} of {result.total}. Failed: {result.failedCount}.
                      </div>
                    ) : null}
                  </section>
                </div>

                <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-5 py-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Close
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={loadPreview}
                      disabled={!canPreview || loadingPreview}
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-400"
                    >
                      {loadingPreview ? "Loading..." : "Preview recipients"}
                    </button>
                    <button
                      type="button"
                      onClick={sendBlast}
                      disabled={!canSend}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-zinc-300"
                    >
                      {sending ? "Sending..." : "Send email blast"}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
